import { queryKeys } from '@/lib/query-keys';
import { apiError, checkoutClient } from '@/lib/api/ts-rest';
import { HttpError } from '@/lib/api/error';
import { createLogger } from '@/lib/logger';
import type {
  AddLineToCheckoutBody,
  AddSimsToCheckoutBody,
  ConfirmCheckoutBody,
} from '@octave/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTenantSlug } from './useTenantSlug';

const log = createLogger('useCheckoutActions');

export const useAddLineToCheckout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  return useMutation<{ success: boolean }, Error, AddLineToCheckoutBody>({
    mutationFn: async (body) => {
      const res = await checkoutClient.addLineToCheckout({ body });
      if (res.status !== 201) throw new Error('Failed to add line');
      return res.body;
    },
    onSuccess: () => {
      toast.success('Ligne ajoutée au panier');
      navigate(`/o/${tenantSlug}/lines`);
    },
    onError: (error) => {
      log.error('Error adding line to checkout', error);
      toast.error("Erreur lors de l'ajout de la ligne au panier");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
    },
  });
};

export const useRemoveItemFromCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { itemId: string }>({
    mutationFn: async (body) => {
      const res = await checkoutClient.removeItemFromCheckout({ body });
      if (res.status !== 201) throw new Error('Failed to remove item');
      return res.body;
    },
    onSuccess: () => {
      toast.success('Article supprimé du panier');
    },
    onError: (e) => {
      log.error('Remove item from checkout error', e);
      toast.error("Erreur lors de la suppression de l'article du panier");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
    },
  });
};

export const useConfirmCheckout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  return useMutation<
    { orderId: string; orderNumber: string },
    Error,
    ConfirmCheckoutBody
  >({
    mutationFn: async (body) => {
      const res = await checkoutClient.confirmCheckout({ body });
      if (res.status !== 201) throw apiError(res, 'Failed to confirm checkout');
      return res.body;
    },
    onSuccess: (data) => {
      //reload the page
      navigate(`/o/${tenantSlug}/orders/${data.orderId}/confirmation`);
      window.location.reload();
    },
    onError: (error) => {
      log.error('Error confirming checkout', error);
      // A 409 says what to fix in the cart (an eSIM to recharge deleted
      // since, a stay already started), in French.
      toast.error(
        error instanceof HttpError && error.status === 409
          ? error.message
          : 'Erreur lors de la confirmation de la commande',
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.pendingCount() });
    },
  });
};

export const useAddSimsToCheckout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  return useMutation<{ success: boolean }, Error, AddSimsToCheckoutBody>({
    mutationFn: async (body) => {
      const res = await checkoutClient.addSimsToCheckout({ body });
      if (res.status !== 201) throw new Error('Failed to add SIMs');
      return res.body;
    },
    onSuccess: () => {
      toast.success('SIMs ajoutées au panier');
      navigate(`/o/${tenantSlug}/sim-stock`);
    },
    onError: (error) => {
      log.error('Error adding SIMs to checkout', error);
      toast.error("Erreur lors de l'ajout des SIMs au panier");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
    },
  });
};
